import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatNumber } from "@/utils/numberFormatter";
import { FootprintSummary } from "@/types/results";
import { BarChart3 } from "lucide-react";

interface ProductComparisonTableProps {
  data: FootprintSummary[];
  className?: string;
}

export const ProductComparisonTable = ({ data, className }: ProductComparisonTableProps) => {
  const { t } = useLanguage();

  // Sort by product name alphabetically
  const sortedData = [...data].sort((a, b) => {
    return a.product_name.localeCompare(b.product_name);
  });

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-indigo-600" />
          {t("results.comparison.resultsByProduct")}
        </CardTitle>
        <p className="text-sm text-gray-600">{t("results.comparison.description")}</p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left p-3 font-medium text-gray-700">{t("results.comparison.name")}</th>
                <th className="text-left p-3 font-medium text-gray-700">{t("results.comparison.stoneType")}</th>
                <th className="text-left p-3 font-medium text-gray-700">{t("results.comparison.productType")}</th>
                <th className="text-center p-3 font-medium text-gray-700">{t("results.comparison.functionalUnit")}</th>
                <th className="text-left p-3 font-medium text-gray-700">{t("results.comparison.year")}</th>
                <th className="text-right p-3 font-medium text-gray-700">
                  {t("results.comparison.carbon")}
                  <div className="text-xs font-normal text-gray-500">{t("results.comparison.annualTotal")}</div>
                </th>
                <th className="text-right p-3 font-medium text-gray-700">
                  {t("results.comparison.hydric")}
                  <div className="text-xs font-normal text-gray-500">{t("results.comparison.annualTotal")}</div>
                </th>
                <th className="text-right p-3 font-medium text-gray-700">{t("results.comparison.carbonPerUnit")}</th>
                <th className="text-right p-3 font-medium text-gray-700">{t("results.comparison.hydricPerUnit")}</th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map((item, index) => {
                // Per unit values (these are already per functional unit)
                const carbonPerUnit = item.carbon_footprint || 0;
                const hydricPerUnit = item.hydric_footprint || 0;

                // Calculate annual totals
                const annualCarbonTotal = carbonPerUnit * (item.annual_production || 0);
                const annualHydricTotal = hydricPerUnit * (item.annual_production || 0);

                return (
                  <tr
                    key={item.id}
                    className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                      index % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                    }`}
                  >
                    {/* Product Name */}
                    <td className="p-3">
                      <div className="font-medium text-gray-900">{item.product_name}</div>
                    </td>

                    {/* Stone Type */}
                    <td className="p-3">
                      <div className="text-sm text-gray-700">{item.stone_type_name || t("common.notSpecified")}</div>
                    </td>

                    {/* Product Type */}
                    <td className="p-3">
                      <Badge variant="secondary" className="text-xs">
                        {t(`${item.product_type}`)}
                      </Badge>
                    </td>

                    {/* Functional Unit */}
                    <td className="p-3 text-center">
                      <div className="text-sm font-medium text-gray-700">
                        {formatNumber(item.functional_unit)} {item.unit}
                      </div>
                    </td>

                    {/* Year */}
                    <td className="p-3">
                      <span className="text-sm font-medium">{item.year}</span>
                    </td>

                    {/* Carbon Footprint - Annual Total */}
                    <td className="p-3 text-right">
                      <div className="font-semibold text-teal-700">{formatNumber(annualCarbonTotal)}</div>
                      <div className="text-xs text-gray-500">{t("results.carbon.units.kgCO2e")}</div>
                    </td>

                    {/* Hydric Footprint - Annual Total */}
                    <td className="p-3 text-right">
                      <div className="font-semibold text-cyan-700">{formatNumber(annualHydricTotal)}</div>
                      <div className="text-xs text-gray-500">{t("results.hydric.units.m3")}</div>
                    </td>

                    {/* Carbon per Unit */}
                    <td className="p-3 text-right">
                      <div className="text-sm text-teal-600">{formatNumber(carbonPerUnit)}</div>
                      <div className="text-xs text-gray-500">
                        {t("results.carbon.units.kgCO2e")}/{item.unit}
                      </div>
                    </td>

                    {/* Hydric per Unit */}
                    <td className="p-3 text-right">
                      <div className="text-sm text-cyan-600">{formatNumber(hydricPerUnit)}</div>
                      <div className="text-xs text-gray-500">
                        {t("results.hydric.units.m3")}/{item.unit}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};
